import { CommonModule } from "@angular/common";
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Inject,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { SelectModule } from "primeng/select";
import { TextareaModule } from "primeng/textarea";
import { SnackBarService } from "../../../app/services/snack-bar.service";
import { DateTimeHelper } from "../../helpers/datetime-converter.helper";
import {
  applyPdvFeeAbsorptionSync,
  bindPdvFeeAbsorptionSync,
  PdvFeeAbsorptionControls,
} from "../../helpers/pdv-fee-absorption/pdv-fee-absorption.helper";
import { ButtonComponent } from "../button/button.component";
import { EventDate } from "../cards/card-manage-event/types/event-date.type";
import { DialogHeaderComponent } from "../dialog-header/dialog-header.component";
import { TicketBatch } from "./types/ticket-batch.type";
import { Ticket } from "./types/ticket.type";
import {
  ticketsDateRangeValidatorFormCreate,
  ticketsDateRangeValidatorFormUpdate,
} from "./validators/tickets-date.validator";
import { ticketsQuantityValidator } from "./validators/tickets-quantity.validator";
import { minimumTicketPrice } from "../../helpers/payment-minimum.helper";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  batchTransitionDates,
  hasBatchPredecessor,
} from "../../helpers/batch-transition/batch-transition.helper";

interface OptionSelect {
  name: string;
  value: number;
}

export interface ModalTicketsDialogData {
  isTicketFree?: boolean;
  selectedBatch?: number;
  selectedTicket?: number;
  eventDate: EventDate;
  ticketBatches: TicketBatch[];
  editBatch?: boolean;
}

@Component({
  selector: "app-modal-tickets",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TextareaModule,
    ButtonComponent,
    SelectModule,
    MatDialogModule,
    DialogHeaderComponent,
  ],
  templateUrl: "./modal-tickets.component.html",
  styleUrl: "./modal-tickets.component.scss",
})
export class ModalTicketsComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<ModalTicketsComponent>);

  private readonly formBuilder = inject(FormBuilder);
  private readonly snackbarService = inject(SnackBarService);
  private readonly destroyRef = inject(DestroyRef);

  ticketsForm!: FormGroup;
  @Output() ticketsEvent: EventEmitter<TicketBatch[]> = new EventEmitter<
    TicketBatch[]
  >();
  isTicketFree: boolean;
  selectedBatch: number | undefined;
  selectedTicket: number | undefined;
  eventDate: EventDate;
  ticketBatches: TicketBatch[];
  editBatch: boolean;

  constructor(@Inject(MAT_DIALOG_DATA) public data: ModalTicketsDialogData) {
    this.isTicketFree = data.isTicketFree ?? false;
    this.selectedBatch = data.selectedBatch;
    this.selectedTicket = data.selectedTicket;
    this.eventDate = data.eventDate;
    this.ticketBatches = data.ticketBatches;
    this.editBatch = data.editBatch ?? false;
  }

  optionsSelectBatch!: OptionSelect[];

  ngOnInit() {
    let selectedTicketData = undefined;
    const selectedBatchData = this.ticketBatches[this.selectedBatch ?? 0];

    this.optionsSelectBatch = this.ticketBatches.map((_, index) => ({
      name: `Lote ${index + 1}`,
      value: index,
    }));

    if (this.selectedBatch !== undefined && this.selectedTicket !== undefined) {
      selectedTicketData =
        this.ticketBatches[this.selectedBatch].tickets[this.selectedTicket];
    }

    this.ticketsForm = this.formBuilder.group({
      transitionMode: [
        selectedBatchData?.transition_mode ?? "date_or_sold_out",
      ],
      selectedBatch: [
        this.selectedBatch ? this.selectedBatch : 0,
        [Validators.required],
      ],
      id: [
        selectedTicketData?.id ? selectedTicketData.id : "",
        this.editBatch || selectedTicketData?.id == undefined
          ? []
          : [Validators.required],
      ],
      ticketTitle: [
        selectedTicketData?.title ? selectedTicketData.title : "",
        this.editBatch ? [] : [Validators.required],
      ],
      ticketDescription: [
        selectedTicketData?.description ? selectedTicketData?.description : "",
        this.editBatch ? [] : [Validators.required],
      ],
      quantity: [
        selectedTicketData?.quantity ? selectedTicketData.quantity : 0,
        this.editBatch ? [] : [Validators.required, Validators.min(1)],
      ],
      price: this.isTicketFree
        ? [{ value: "GRATIS", disabled: true }]
        : [
            selectedTicketData?.price ? selectedTicketData.price : 0,
            this.editBatch ? [] : [Validators.required, minimumTicketPrice],
          ],
      absorbsPlatformFee: [selectedBatchData?.absorbs_platform_fee ?? false],
      absorbsProcessingFee: [
        selectedBatchData?.absorbs_processing_fee ?? false,
      ],
      soldAtPdv: [selectedBatchData?.sold_at_pdv ?? false],
      halfPrice: [false],
      ticketsDateGroup: this.formBuilder.group(
        {
          start_date: [this.eventDate.startDate, Validators.required],
          start_time: [this.eventDate.startTime, Validators.required],
          end_date: [this.eventDate.endDate, Validators.required],
          end_time: [this.eventDate.endTime, Validators.required],
        },
        {
          validators: [
            (group) => {
              if (
                this.ticketsForm?.get("transitionMode")?.value ===
                "sold_out_only"
              ) {
                if (!this.openingEditable) return null;
                const date = group.get("start_date")?.value;
                const time = group.get("start_time")?.value;
                if (!date || !time) return null;
                const start = new Date(`${date}T${time}`);
                return start >
                  new Date(
                    `${this.eventDate.endDate}T${this.eventDate.endTime}`,
                  )
                  ? { startDateAfterEventEnd: true }
                  : null;
              }
              if (
                !["start_date", "start_time", "end_date", "end_time"].every(
                  (key) => group.get(key)?.value,
                )
              )
                return null;
              const validator =
                !this.editBatch || selectedTicketData?.batch_id
                  ? [ticketsDateRangeValidatorFormUpdate(this.eventDate)]
                  : [ticketsDateRangeValidatorFormCreate(this.eventDate)];
              return validator[0](group);
            },
          ],
        },
      ),
      minTickets: [
        selectedTicketData?.min_per_buyer
          ? selectedTicketData.min_per_buyer
          : 1,
        this.editBatch ? [] : [Validators.required, Validators.min(1)],
      ],
      maxTickets: [
        selectedTicketData?.max_per_buyer
          ? selectedTicketData.max_per_buyer
          : 5,
        this.editBatch ? [] : [Validators.required, ticketsQuantityValidator()],
      ],
      availability: [
        "general public",
        this.editBatch ? [] : Validators.required,
      ],
    });

    if (this.selectedBatch !== undefined) {
      const batchStartDate = DateTimeHelper.fromIsoFormat(
        this.ticketBatches[this.selectedBatch].sale_start,
      );
      const batchEndDate = DateTimeHelper.fromIsoFormat(
        this.ticketBatches[this.selectedBatch].sale_end,
      );

      this.ticketsForm.patchValue({
        ticketsDateGroup: {
          start_date: batchStartDate.date,
          start_time: batchStartDate.time,
          end_date: batchEndDate.date,
          end_time: batchEndDate.time,
        },
      });
    }
    this.syncTransitionControls();
    this.ticketsForm
      .get("transitionMode")!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncTransitionControls());
    const pdvFeeSync = bindPdvFeeAbsorptionSync(this.pdvFeeControls);
    this.destroyRef.onDestroy(() => pdvFeeSync.unsubscribe());
  }

  get soldOutOnly(): boolean {
    return this.ticketsForm.get("transitionMode")?.value === "sold_out_only";
  }

  private get currentBatch(): TicketBatch | undefined {
    return this.ticketBatches[
      Number(this.ticketsForm.get("selectedBatch")?.value)
    ];
  }

  get openingEditable(): boolean {
    return (
      !this.soldOutOnly ||
      !hasBatchPredecessor(this.ticketBatches, this.currentBatch)
    );
  }

  private syncTransitionControls(): void {
    const group = this.ticketsForm.get("ticketsDateGroup")!;
    if (!this.openingEditable) {
      const start = DateTimeHelper.fromIsoFormat(
        batchTransitionDates(
          "sold_out_only",
          true,
          null,
          null,
          this.currentBatch,
        ).sale_start,
      );
      group.patchValue(
        { start_date: start.date, start_time: start.time },
        { emitEvent: false },
      );
    }
    for (const key of ["start_date", "start_time"]) {
      if (this.openingEditable) group.get(key)!.enable({ emitEvent: false });
      else group.get(key)!.disable({ emitEvent: false });
    }
    for (const key of ["end_date", "end_time"]) {
      if (this.soldOutOnly) group.get(key)!.disable({ emitEvent: false });
      else group.get(key)!.enable({ emitEvent: false });
    }
    group.updateValueAndValidity();
    applyPdvFeeAbsorptionSync(this.pdvFeeControls, "batchStartDate");
  }

  private get pdvFeeControls(): PdvFeeAbsorptionControls {
    return {
      absorbsPlatformFee: this.ticketsForm.get("absorbsPlatformFee")!,
      absorbsProcessingFee: this.ticketsForm.get("absorbsProcessingFee")!,
      soldAtPdv: this.ticketsForm.get("soldAtPdv")!,
      batchStartDate: this.ticketsForm.get("ticketsDateGroup.start_date")!,
      eventStartDate: this.eventDate.startDate,
    };
  }

  closeModal() {
    this.dialogRef.close();
  }

  saveTicket() {
    if (this.ticketsForm.valid) {
      applyPdvFeeAbsorptionSync(this.pdvFeeControls, "soldAtPdv");
      const ticketsFormValues: Ticket = this.ticketsForm.getRawValue();
      const selectedBatch = +ticketsFormValues.selectedBatch;
      const batchIndex = ticketsFormValues.selectedBatch;
      const startDateIso = this.openingEditable
        ? DateTimeHelper.toIsoFormat(
            ticketsFormValues.ticketsDateGroup.start_date,
            ticketsFormValues.ticketsDateGroup.start_time,
          )
        : null;
      const endDateIso = !this.soldOutOnly
        ? DateTimeHelper.toIsoFormat(
            ticketsFormValues.ticketsDateGroup.end_date,
            ticketsFormValues.ticketsDateGroup.end_time,
          )
        : null;
      const dates = batchTransitionDates(
        ticketsFormValues.transitionMode,
        hasBatchPredecessor(this.ticketBatches, this.currentBatch),
        startDateIso,
        endDateIso,
        this.currentBatch,
      );

      if (selectedBatch > this.ticketBatches.length) {
        this.addNewTicketBatch(
          dates.sale_start,
          dates.sale_end,
          ticketsFormValues,
        );
      } else {
        this.updateTicketBatch(
          dates.sale_start,
          dates.sale_end,
          batchIndex,
          ticketsFormValues,
        );
      }

      this.snackbarService.openSnackBar(
        "Ingresso criado com sucesso",
        "Fechar",
        "success",
      );
      this.ticketsEvent.emit(this.ticketBatches);
      this.dialogRef.close();
    } else {
      this.snackbarService.openSnackBar(
        "Formulario não tem dados validos",
        "Fechar",
        "error",
      );
      this.ticketsForm.markAllAsTouched();
    }
  }

  onBatchChange(newValue: OptionSelect) {
    const selectedIndex = newValue.value;
    const selectedBatch = this.ticketBatches[selectedIndex];

    if (!selectedBatch) {
      return;
    }

    const batchStartDate = DateTimeHelper.fromIsoFormat(
      selectedBatch.sale_start,
    );
    const batchEndDate = DateTimeHelper.fromIsoFormat(selectedBatch.sale_end);
    this.ticketsForm.patchValue(
      {
        selectedBatch: selectedIndex,
        transitionMode: selectedBatch.transition_mode ?? "date_or_sold_out",
        absorbsPlatformFee: selectedBatch.absorbs_platform_fee ?? false,
        absorbsProcessingFee: selectedBatch.absorbs_processing_fee ?? false,
        soldAtPdv: selectedBatch.sold_at_pdv ?? false,
        ticketsDateGroup: {
          start_date: batchStartDate.date,
          start_time: batchStartDate.time,
          end_date: batchEndDate.date,
          end_time: batchEndDate.time,
        },
      },
      { emitEvent: false },
    );
    this.syncTransitionControls();

    if (selectedBatch.sold_at_pdv) {
      applyPdvFeeAbsorptionSync(this.pdvFeeControls, "soldAtPdv");
    }
  }

  private addNewTicketBatch(
    startDateIso: string | null,
    endDateIso: string | null,
    ticketData?: Ticket,
  ) {
    this.ticketBatches.push({
      sale_start: startDateIso,
      sale_end: endDateIso,
      transition_mode: ticketData?.transitionMode ?? "date_or_sold_out",
      absorbs_platform_fee: ticketData?.absorbsPlatformFee ?? false,
      absorbs_processing_fee: ticketData?.absorbsProcessingFee ?? false,
      sold_at_pdv: ticketData?.soldAtPdv ?? false,
      tickets: [],
    });
    if (ticketData !== undefined) {
      this.ticketBatches[this.ticketBatches.length - 1].tickets.push({
        title: ticketData.ticketTitle,
        description: ticketData.ticketDescription,
        min_per_buyer: ticketData.minTickets,
        max_per_buyer: ticketData.maxTickets,
        quantity: ticketData.quantity,
        price: isNaN(+ticketData.price) ? 0 : +ticketData.price,
        availability: ticketData.availability,
      });
    }
  }

  private updateTicketBatch(
    startDateIso: string | null,
    endDateIso: string | null,
    batchIndex: number,
    ticketData?: Ticket,
  ) {
    const selectedBatch = this.ticketBatches[batchIndex];
    selectedBatch.sale_start = startDateIso;
    selectedBatch.sale_end = endDateIso;
    selectedBatch.transition_mode =
      ticketData?.transitionMode ?? "date_or_sold_out";
    selectedBatch.absorbs_platform_fee =
      ticketData?.absorbsPlatformFee ?? false;
    selectedBatch.absorbs_processing_fee =
      ticketData?.absorbsProcessingFee ?? false;
    selectedBatch.sold_at_pdv = ticketData?.soldAtPdv ?? false;

    if (ticketData && ticketData?.ticketTitle != "") {
      if (this.selectedTicket != undefined) {
        selectedBatch.tickets[this.selectedTicket] = {
          id: ticketData?.id ? ticketData.id : undefined,
          title: ticketData.ticketTitle,
          description: ticketData.ticketDescription,
          min_per_buyer: ticketData.minTickets,
          max_per_buyer: ticketData.maxTickets,
          quantity: ticketData.quantity,
          price: isNaN(+ticketData.price) ? 0 : +ticketData.price,
          availability: ticketData.availability,
        };
      } else {
        selectedBatch.tickets.push({
          id: ticketData?.id ? ticketData.id : undefined,
          title: ticketData.ticketTitle,
          description: ticketData.ticketDescription,
          min_per_buyer: ticketData.minTickets,
          max_per_buyer: ticketData.maxTickets,
          quantity: ticketData.quantity,
          price: isNaN(+ticketData.price) ? 0 : +ticketData.price,
          availability: ticketData.availability,
        });
      }
    }
  }

  getTaxString() {
    const taxRate = 0.1;
    const ticketPrice = this.ticketsForm.value.price;
    const taxValue = ticketPrice * taxRate;

    if (this.absorbsPlatformFee) {
      return `O cliente paga R$${ticketPrice}; a taxa da plataforma de R$${taxValue} é absorvida pelo produtor`;
    }

    return `R$${ticketPrice} + Taxa(R$${taxValue})`;
  }

  getFinalPriceString() {
    const taxRate = 0.1;
    const ticketPrice = Number(this.ticketsForm.value.price);
    const taxValue = ticketPrice * taxRate;

    return this.absorbsPlatformFee ? ticketPrice : ticketPrice + taxValue;
  }

  get absorbsPlatformFee(): boolean {
    return !!this.ticketsForm?.get("absorbsPlatformFee")?.value;
  }
}
